import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiMapPin, FiPhone, FiMail, FiSend, FiClock, FiCheckCircle } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: 'garage',
    sqft: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setFormData({ name: '', email: '', phone: '', service: 'garage', sqft: '', message: '' });
  };

  const contactItems = [
    { icon: FiPhone, title: 'Call or Text', info: '(954) 328-5964', sub: 'Mon – Sat, 7am – 7pm', href: 'tel:9543285964' },
    { icon: FiMail, title: 'Email Us', info: 'quotes@allstarepoxy.com', sub: 'Reply within 2 hours', href: 'mailto:quotes@allstarepoxy.com' },
    { icon: FiMapPin, title: 'Our Office', info: '3400 Bayview Dr', sub: 'Fort Lauderdale, FL 33306', href: null },
    { icon: FiClock, title: 'Business Hours', info: 'Mon – Sat: 7am – 7pm', sub: 'Sunday: Closed', href: null },
  ];

  const inputClass = "w-full px-5 py-4 rounded-xl bg-white border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all text-base";
  const labelClass = "block text-xs font-bold text-gray-500 mb-2 uppercase tracking-widest";

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div className="relative h-80 md:h-[480px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvcG5n/5c980b02fae98bba170d82c1e5aa61c5.png" 
            alt="Contact All Star Epoxy" 
            className="w-full h-full object-cover scale-105"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-red-900/80 via-black/50 to-black/80" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="text-red-400 text-sm font-black tracking-[0.2em] uppercase mb-4 block">Let's Talk</span>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-white tracking-tight">Get a Free Quote</h1>
          </motion.div>
        </div>
      </div>

      {/* Contact Info Bar */}
      <div className="bg-red-600 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {contactItems.map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center gap-3"
              >
                <SafeIcon icon={item.icon} className="text-white/70 text-xl flex-shrink-0" />
                <div>
                  <p className="text-white/60 text-xs font-bold uppercase tracking-wider">{item.title}</p>
                  {item.href ? (
                    <a href={item.href} className="text-white font-bold text-sm hover:text-red-200 transition-colors">{item.info}</a>
                  ) : (
                    <p className="text-white font-bold text-sm">{item.info}</p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="grid lg:grid-cols-3 gap-12">
          
          {/* Left Sidebar */}
          <div className="space-y-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-50 border border-gray-200 rounded-[2rem] p-10 shadow-sm"
            >
              <span className="text-red-600 text-xs font-black tracking-[0.2em] uppercase mb-3 block">Why Choose Us</span>
              <h3 className="text-2xl font-black text-gray-900 mb-8">What Happens Next</h3>
              <ol className="space-y-6">
                {[
                  { step: '01', text: 'We review your request within 2 hours.' },
                  { step: '02', text: 'We schedule a free on-site visit.' },
                  { step: '03', text: 'You receive a detailed written quote.' },
                  { step: '04', text: 'We schedule your install date.' },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <span className="text-3xl font-black text-red-100 leading-none w-10 flex-shrink-0">{item.step}</span>
                    <p className="text-gray-600 font-medium leading-relaxed pt-1">{item.text}</p>
                  </li>
                ))}
              </ol>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="bg-red-600 rounded-[2rem] p-10 shadow-lg"
            >
              <SafeIcon icon={FiPhone} className="text-white/60 text-3xl mb-4" />
              <h4 className="text-white font-black text-xl mb-3">Prefer to Call?</h4>
              <p className="text-red-100 text-sm leading-relaxed mb-6 font-light">
                Speak directly with our team. We're available Mon–Sat, 7am to 7pm.
              </p>
              <a 
                href="tel:9543285964"
                className="inline-block w-full text-center py-4 bg-white text-red-600 font-black rounded-xl hover:bg-red-50 transition-colors shadow-md text-lg"
              >
                (954) 328-5964
              </a>
            </motion.div>
          </div>

          {/* Form */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-2 bg-white border border-gray-200 rounded-[2rem] p-8 md:p-14 shadow-lg"
          >
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-24">
                <div className="w-24 h-24 rounded-full bg-red-50 flex items-center justify-center mb-8 border border-red-100">
                  <SafeIcon icon={FiCheckCircle} className="text-red-600 text-5xl" />
                </div>
                <h3 className="text-4xl font-black text-gray-900 mb-6">Message Received!</h3>
                <p className="text-gray-500 text-xl max-w-md font-light leading-relaxed">
                  Thank you! A member of our team will contact you within 2 business hours to discuss your project.
                </p>
                <button 
                  onClick={() => setSubmitted(false)}
                  className="mt-10 px-8 py-4 bg-red-600 text-white font-black rounded-full hover:bg-red-700 transition-colors shadow-lg"
                >
                  Submit Another Request
                </button>
              </div>
            ) : (
              <>
                <span className="text-red-600 text-xs font-black tracking-[0.2em] uppercase mb-3 block">Free Estimate</span>
                <h3 className="text-3xl font-black text-gray-900 mb-2">Tell Us About Your Project</h3>
                <p className="text-gray-500 mb-10 font-light">Fill out the form and we'll be in touch within 2 hours.</p>

                <form onSubmit={handleSubmit} className="space-y-7">
                  <div className="grid md:grid-cols-2 gap-7">
                    <div>
                      <label className={labelClass}>Full Name *</label>
                      <input 
                        type="text" 
                        required
                        className={inputClass}
                        placeholder="John Smith"
                        value={formData.name}
                        onChange={e => setFormData({...formData, name: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Phone Number *</label>
                      <input 
                        type="tel" 
                        required
                        className={inputClass}
                        placeholder="(954) 000-0000"
                        value={formData.phone}
                        onChange={e => setFormData({...formData, phone: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-7">
                    <div>
                      <label className={labelClass}>Email Address *</label>
                      <input 
                        type="email" 
                        required
                        className={inputClass}
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={e => setFormData({...formData, email: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Service Type *</label>
                      <select 
                        className={inputClass}
                        value={formData.service}
                        onChange={e => setFormData({...formData, service: e.target.value})}
                      >
                        <option value="garage">Residential Garage</option>
                        <option value="warehouse">Commercial Warehouse</option>
                        <option value="custom">Custom / Metallic Epoxy</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>Approximate Square Footage</label>
                    <input 
                      type="text" 
                      className={inputClass}
                      placeholder="e.g. 600 sq ft garage or 15,000 sq ft warehouse"
                      value={formData.sqft}
                      onChange={e => setFormData({...formData, sqft: e.target.value})}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>Project Details *</label>
                    <textarea 
                      rows="5" 
                      required
                      className={`${inputClass} resize-none`}
                      placeholder="Describe your space, current floor condition, desired finish..."
                      value={formData.message}
                      onChange={e => setFormData({...formData, message: e.target.value})}
                    />
                  </div>

                  <button 
                    type="submit"
                    className="w-full py-5 bg-red-600 hover:bg-red-700 text-white font-black rounded-xl transition-all shadow-lg flex items-center justify-center gap-3 text-lg"
                  >
                    Send My Request
                    <SafeIcon icon={FiSend} />
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;